import { shouldGrantConsentWithoutBanner } from './ga4Env';
import { getGtag } from './gtag';

type CookieYesConsentDetail = {
  accepted?: string[];
  rejected?: string[];
};

const consentState = (granted: boolean): 'granted' | 'denied' =>
  granted ? 'granted' : 'denied';

/** Map CookieYes categories → Google Consent Mode v2 (gtag). */
export const applyGtagConsentFromCookieYes = (
  detail: CookieYesConsentDetail
): void => {
  const gtag = getGtag();
  if (!gtag) return;

  const accepted = new Set(detail.accepted ?? []);

  gtag('consent', 'update', {
    analytics_storage: consentState(accepted.has('analytics')),
    ad_storage: consentState(accepted.has('advertisement')),
    ad_user_data: consentState(accepted.has('advertisement')),
    ad_personalization: consentState(accepted.has('advertisement')),
    functionality_storage: consentState(accepted.has('functional')),
    personalization_storage: consentState(
      accepted.has('performance') || accepted.has('functional')
    ),
  });
};

/**
 * Stand in for CookieYes on localhost and on QA hosts where the banner is not
 * wired up, so DebugView and `gtag('get', 'client_id')` work. Without this,
 * analytics_storage stays denied, gtag falls back to cookieless pings and the
 * id lookup never calls back — leaving client_id/session_id null on every event.
 *
 * Deliberately omits the advertisement categories: analytics_storage is all
 * DebugView and the id lookup need, so ad tags stay off. Returns whether the
 * grant was applied.
 */
export const applyGtagConsentWithoutBanner = (): boolean => {
  if (!shouldGrantConsentWithoutBanner()) return false;

  applyGtagConsentFromCookieYes({
    accepted: ['necessary', 'functional', 'analytics'],
  });

  console.info(
    '[ga4] Consent granted without CookieYes (non-production build) — analytics only, ads denied'
  );

  return true;
};

/** Apply gtag consent from an existing CookieYes cookie (returning visitors). */
export const applyGtagConsentFromDocumentCookie = (): void => {
  if (typeof document === 'undefined') return;

  const match = document.cookie.match(/(?:^|;\s*)cookieyes-consent=([^;]*)/);
  if (!match) return;

  try {
    const value = decodeURIComponent(match[1]);
    const accepted: string[] = [];
    for (const part of value.split(',')) {
      const colonIndex = part.indexOf(':');
      if (colonIndex === -1) continue;
      const key = part.slice(0, colonIndex).trim().toLowerCase();
      const raw = part.slice(colonIndex + 1).trim().toLowerCase();
      if (raw === 'yes' && key !== 'consentid' && key !== 'consent' && key !== 'action') {
        accepted.push(key);
      }
    }
    if (accepted.length) {
      applyGtagConsentFromCookieYes({ accepted });
    }
  } catch {
    /* ignore malformed cookie */
  }
};

export const initGa4ConsentSync = (): (() => void) => {
  if (!applyGtagConsentWithoutBanner()) {
    applyGtagConsentFromDocumentCookie();
  }

  const onConsentUpdate = (event: Event) => {
    const detail = (event as CustomEvent<CookieYesConsentDetail>).detail;
    if (!detail) return;
    applyGtagConsentFromCookieYes(detail);
  };

  document.addEventListener('cookieyes_consent_update', onConsentUpdate);
  return () => {
    document.removeEventListener('cookieyes_consent_update', onConsentUpdate);
  };
};
